import {observable} from "@nx-js/observer-util";
import {Services} from "../Services";
import {IOfferModel} from "../../models/offer/IOfferModel";
import {INetworkComponent} from "../../network/types/INetworkComponent";
import {Network} from "../../network/Network";
import {ProductType} from "../../models/product/ProductType";
import {HttpStatus} from "../../network/status/HttpStatus";
import {IOfferProductTemplatesModel} from "../../models/offer/product/templates/IOfferProductTemplatesModel";
import {OfferProductTemplateService} from "./product/template/OfferProductTemplateService";
import {IOfferDetailsModel} from "../../models/offer/IOfferDetailsModel";
import {IOfferQuoteSubscriptionOutgoing} from "../../models/offer/quote/IOfferQuoteSubscriptionOutgoing";
import {ErrorCode} from "../../network/status/error/ErrorCode";
import {ISiteAddressModel} from "../../models/site/address/ISiteAddressModel";
import {Resources} from "../../resources/Resources";
import {IOfferPaymentSecretsModel} from "../../models/offer/payment/IOfferPaymentSecretsModel";
import {OfferState} from "../../models/offer/OfferState";
import {EntityService} from "../entity/EntityService";
import {OfferProductLevel} from "../../models/offer/product/OfferProductLevel";
import {IOfferAdminStatsIncoming} from "../../models/offer/IOfferAdminStatsIncoming";
import {IOfferAdminModel} from "../../models/offer/IOfferAdminModel";
import {IOfferActiveSubscriptionModel} from "../../models/offer/IOfferActiveSubscriptionModel";
import {IOfferLimitsUpdateOutgoing} from "../../models/offer/IOfferLimitsUpdateOutgoing";
import {IOfferLimitsBatchUpdateOutgoing} from "../../models/offer/IOfferLimitsBatchUpdateOutgoing";
import {OfferProductTemplateType} from "../../models/offer/product/templates/OfferProductTemplateType";
import {IOfferProductTemplateModel} from "../../models/offer/product/templates/IOfferProductTemplateModel";
import {OfferProductSolutionType} from "../../models/offer/product/solution/OfferProductSolutionType";
import {AccountLogType} from "../../models/account/log/AccountLogType";
import {queryString} from "../../sedestral/framework/utilities/QueryString";
import {IOfferStartingPriceModel} from "../../models/offer/price/IOfferStartingPriceModel";
import {OfferPeriodType} from "../../models/offer/product/OfferPeriodType";
import {currencyByCode} from "../../models/currency/Currencies";
import {numberPrice} from "../../sedestral/framework/utilities/NumberPrice";

export class OfferService {
    public static offers: IOfferModel[] = observable([]);
    private static startingPricePromise: Promise<IOfferStartingPriceModel>;

    public static dispose(): void {
        this.offers = observable([]);
    }

    public static init(): void {

    }

    // Loggé : le back résout la devise depuis le site (pattern) — le front ne passe RIEN. Déloggé : on passe le country.
    public static async findAll(component?: INetworkComponent, country?: string): Promise<IOfferProductTemplatesModel> {
        const query = country ? `?country=${country}` : "";
        let request = await Network.get(ProductType.PANEL, `/offer/templates${query}`, component);
        if (request.status == HttpStatus.OK) {
            let templates: IOfferProductTemplatesModel = request.data;
            templates.templates = OfferProductTemplateService.storeAll(templates.templates);
            return templates;
        }
    }


    public static async active(displayPayment: boolean, component?: INetworkComponent): Promise<IOfferDetailsModel> {
        Services.handleErrors(component, [
            {status: HttpStatus.NOT_FOUND, message: "none"}
        ]);

        let request = await Network.get(ProductType.PANEL, `/offer/active/${displayPayment}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async scheduled(component?: INetworkComponent): Promise<IOfferDetailsModel> {
        Services.handleErrors(component, [
            {status: HttpStatus.NOT_FOUND, message: "none"}
        ]);

        let request = await Network.get(ProductType.PANEL, `/offer/schedule`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async cancel(offerId: string, isCancel: boolean, component?: INetworkComponent): Promise<boolean> {
        Services.handleErrors(component, [
            {
                errorCode: ErrorCode.TASK_IN_PROGRESS,
                message: Resources.t("words.taskInProgressOnCurrentOffer")
            },
            {
                errorCode: ErrorCode.OFFER_CANCELLED_EXPIRED,
                message: Resources.t("words.offerCancelledExpired")
            },
            {status: HttpStatus.NOT_FOUND, message: "none"}
        ]);

        let request = await Network.post(ProductType.PANEL, `/offer/cancel/${offerId}/${isCancel}`, {}, component);
        return request.status == HttpStatus.OK;
    }

    public static async getDateCancel(offerId: string, component?: INetworkComponent): Promise<number> {
        Services.handleErrors(component, [
            {status: HttpStatus.NOT_FOUND, message: "none"}
        ]);

        let request = await Network.get(ProductType.PANEL, `/offer/cancel/estimateDate/${offerId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }

        return undefined;
    }

    public static async saveEstimateLog(offerId: string, logType: number, value: string, component?: INetworkComponent): Promise<boolean> {
        Services.handleErrors(component, [
            {status: HttpStatus.NOT_FOUND, message: "none"}
        ]);

        let request = await Network.post(ProductType.PANEL, `/offer/cancel/estimateLog/${offerId}/${logType}`, {value}, component);
        return request.status == HttpStatus.OK;
    }

    /**
     * La première offre payante, telle que la page des offres l'affiche : son nom, son prix au mois,
     * et ce même mois payé à l'année. Lu une fois par session, les offres ne bougent pas d'ici là.
     */
    public static async startingPrice(): Promise<IOfferStartingPriceModel> {
        if (!OfferService.startingPricePromise) {
            OfferService.startingPricePromise = OfferService.findAll().then(templates => {
                let cheapest = templates?.templates
                    ?.filter(template => template.level == OfferProductLevel.PREMIUM && template.enabled && template.bundle?.priceInitial > 0)
                    .sort((first, second) => first.bundle.priceInitial - second.bundle.priceInitial)[0];
                if (!cheapest) {
                    return undefined;
                }

                let currency = currencyByCode(templates.currency);
                let advantage = templates.periods?.find(period => period.periodType == OfferPeriodType.ANNUAL)?.advantagePercentage ?? 0;
                let roundedPrice = (centimes: number) => numberPrice(Math.round(centimes / 100) * 100, currency, false);
                let articles = cheapest.bundle.solutions
                    ?.find(solution => solution.type == OfferProductSolutionType.AI_CONTENT)?.quantityInitial ?? 0;
                return {
                    name: Resources.t(`words.offerName${cheapest.type}`),
                    monthly: roundedPrice(cheapest.bundle.priceInitial),
                    annualMonthly: roundedPrice(cheapest.bundle.priceInitial * (1 - advantage)),
                    annualAdvantage: Math.round(advantage * 100),
                    articles: articles
                };
            }).catch(() => undefined);
        }

        return OfferService.startingPricePromise;
    }

    /**
     * Mesure le chemin vers le paiement (export bloqué, fenêtre des offres, offre choisie). Rien
     * d'autre n'en dépend : un échec ne doit jamais gêner le client.
     */
    public static track(logType: AccountLogType, value?: string): void {
        Network.getAxios(ProductType.PANEL)
            ?.post(`/offer/track/${logType}`, queryString(value ? {value} : {}))
            .catch(() => undefined);
    }

    //Récupérer le devis en cours quand tu actualises la page
    public static async quote(component?: INetworkComponent): Promise<IOfferDetailsModel> {
        Services.handleErrors(component, [
            {status: HttpStatus.NOT_FOUND, message: "none"}
        ]);

        let request = await Network.get(ProductType.PANEL, `/offer/quote`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    //Créer un devis
    public static async quoteSubscription(offerSubscription: IOfferQuoteSubscriptionOutgoing, component?: INetworkComponent): Promise<IOfferDetailsModel> {
        Services.handleErrors(component, [
            {
                errorCode: ErrorCode.PROMOTION_CODE_INVALID,
                message: Resources.t("words.promoCodeInvalid")
            },
            {
                errorCode: ErrorCode.OFFER_ALREADY_EXISTS,
                message: Resources.t("words.youAlreadyHaveAnOngoingQuote")
            },
            {
                errorCode: ErrorCode.ACTIVE_OFFER_IS_FAILURE_TO_PAY,
                message: Resources.t("words.currentOfferPaymentDefault")
            },
            {
                errorCode: ErrorCode.OFFER_SAME_OFFER_ACTIVE,
                message: Resources.t("words.sameOfferConfiguration")
            }
        ]);

        let request = await Network.postJson(ProductType.PANEL, "/offer/quote/subscription", offerSubscription, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }

        return undefined;
    }

    public static async quoteCancel(component?: INetworkComponent): Promise<boolean> {
        Services.handleErrors(component, [
            {
                errorCode: ErrorCode.TASK_IN_PROGRESS,
                message: Resources.t("words.taskInProgressOnCurrentOffer")
            },
            {status: HttpStatus.NOT_FOUND, message: "none"}
        ]);

        let request = await Network.delete(ProductType.PANEL, `/offer/quote/cancel`, component);
        return request.status == HttpStatus.OK;
    }

    public static async quoteConfirmed(data: {
        offerId: string,
        address: ISiteAddressModel,
        advantageCode: string
    }, component?: INetworkComponent): Promise<IOfferDetailsModel> {
        Services.handleErrors(component, [
            {
                errorCode: ErrorCode.PROMOTION_CODE_INVALID,
                message: Resources.t("words.promoCodeInvalid")
            },
            {
                errorCode: ErrorCode.ACTIVE_OFFER_IS_FAILURE_TO_PAY,
                message: Resources.t("words.currentOfferPaymentDefault")
            },
            {
                errorCode: ErrorCode.TASK_IN_PROGRESS,
                message: Resources.t("words.taskInProgressOnCurrentOffer")
            },
        ]);

        let request = await Network.postJson(ProductType.PANEL, "/offer/quote/confirmed", data, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }

        return undefined;
    }

    public static async deleteCb(component?: INetworkComponent): Promise<boolean> {
        Services.handleErrors(component, [
            {
                errorCode: ErrorCode.OFFER_ACTIVE_EXISTS,
                message: Resources.t("words.cannotDeleteCardDueToActiveOffer")
            },
            {status: HttpStatus.NOT_FOUND, message: Resources.t("words.noCarError")}
        ]);

        let request = await Network.delete(ProductType.PANEL, `/offer/cb`, component);
        return request.status == HttpStatus.OK;
    }

    public static async getOfferPaymentSecrets(component?: INetworkComponent): Promise<IOfferPaymentSecretsModel> {
        Services.handleErrors(component, [
            {status: HttpStatus.BAD_REQUEST, message: Resources.t("words.cannotRetrievePaymentInfo")},
            {status: HttpStatus.NOT_FOUND, message: Resources.t("words.noBankCardRegistered")}
        ]);

        let request = await Network.get(ProductType.PANEL, `/offer/cb/paymentSecret`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }

        return undefined;
    }

    public static async getStats(component?: INetworkComponent): Promise<IOfferAdminStatsIncoming> {
        let request = await Network.get(ProductType.PANEL, "/offer/admin/stats", component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async unlockOrLockOffer(offerId: string, component?: INetworkComponent): Promise<IOfferAdminModel> {
        let request = await Network.get(ProductType.PANEL, `/offer/admin/unlockOrLock/${offerId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async closeOffer(offerId: string, component?: INetworkComponent): Promise<IOfferAdminModel> {
        let request = await Network.get(ProductType.PANEL, `/offer/admin/close/${offerId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async createEssentielQuote(accountId: string, component?: INetworkComponent): Promise<IOfferDetailsModel> {
        let request = await Network.post(ProductType.PANEL, `/offer/admin/quote/essentiel/${accountId}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    //permissions
    public static offerIsFree(): boolean {
        return EntityService.activeOfferProduct?.level == OfferProductLevel.FREE;
    }

    /**
     * Gratuit sans avoir jamais payé : le compte n'a jamais été client. Un résilié redescend lui
     * aussi sur l'offre gratuite, mais ses factures payées restent — les surfaces d'acquisition
     * (discussion de démarrage, menu « L'agence ») ne lui sont pas rendues une seconde fois.
     */
    public static offerIsFreeNeverPaid(): boolean {
        return OfferService.offerIsFree() && !EntityService.activeOfferEverPaid;
    }

    /**
     * Abonnement payant réglé puis résilié, sans offre active aujourd'hui : l'interface
     * fonctionnelle est bloquée. Un compte gratuit qui n'a jamais souscrit n'est pas concerné.
     */
    public static offerExpired(): boolean {
        return EntityService.activeOfferExpired;
    }

    /**
     * Offre active verrouillée pour défaut de paiement (prélèvement refusé, abonnement suspendu) :
     * l'interface fonctionnelle est bloquée jusqu'au règlement. Un déverrouillage manuel par un
     * admin (FAILURE_TO_PAY) laisse l'interface libre.
     */
    public static offerLocked(): boolean {
        return EntityService.activeOfferLocked;
    }

    public static interfaceLocked(): boolean {
        return OfferService.offerExpired() || OfferService.offerLocked();
    }

    public static offerIsEssentiel(): boolean {
        const temp = EntityService.activeOfferProduct?.template?.type;
        return temp == OfferProductTemplateType.OFFER_ESSENTIEL || temp == OfferProductTemplateType.BLOG_ESSENTIEL_LEGACY;
    }

    public static offerIsPro(): boolean {
        const temp = EntityService.activeOfferProduct?.template?.type;
        return temp == OfferProductTemplateType.OFFER_PRO || temp == OfferProductTemplateType.BLOG_PRO_LEGACY;
    }

    public static offerIsSpecialBlog(): boolean {
        const temp = EntityService.activeOfferProduct?.template?.type;
        return temp == OfferProductTemplateType.BLOG_PRO_LEGACY || temp == OfferProductTemplateType.BLOG_ENTREPRISE_LEGACY || temp == OfferProductTemplateType.BLOG_ESSENTIEL_LEGACY;
    }

    public static offerIsMax(): boolean {
        const temp = EntityService.activeOfferProduct?.template?.type;
        return temp == OfferProductTemplateType.OFFER_MAX || temp == OfferProductTemplateType.BLOG_ENTREPRISE_LEGACY;
    }

    public static offerIsEnterprise(): boolean {
        const temp = EntityService.activeOfferProduct?.template?.type;
        return temp == OfferProductTemplateType.OFFER_ENTERPRISE;
    }

    public static hasAddon(type: OfferProductSolutionType): boolean {
        const solution = EntityService.activeOfferProduct?.bundle?.solutions?.find(s => s.type === type);
        return solution?.priceAddons > 0;
    }

    /**
     * Limite de sièges de l'offre active, undefined pour les offres sans solution utilisateurs (illimité).
     */
    public static usersLimit(): number {
        const solution = EntityService.activeOfferProduct?.bundle?.solutions?.find(s => s.type === OfferProductSolutionType.USERS);
        return solution == undefined || solution.quantity <= 0 ? undefined : solution.quantity;
    }

    public static isAgency(): boolean {
        const solution = EntityService.activeOfferProduct?.bundle?.solutions?.find(s => s.type === OfferProductSolutionType.AGENCY);
        return solution != undefined && solution.quantity > 0;
    }

    /**
     * store
     */
    public static storeAll(offers: IOfferModel[]): IOfferModel[] {
        for (let key in offers)
            offers[key] = this.store(offers[key]);

        return Services.storeAll(offers);
    }

    public static store(offer: IOfferModel): IOfferModel {
        offer = Services.store("id", this.offers, offer);
        return offer;
    }

    public static isPaymentFailed(offerDetails?: IOfferDetailsModel): boolean {
        return offerDetails?.offer.state == OfferState.FAILURE_TO_PAY_AND_LOCK
            || offerDetails?.offer.state == OfferState.FAILURE_TO_PAY
            || offerDetails?.offer.state == OfferState.SUSPENDED_AND_LOCK;
    }

    public static async getActiveSubscriptions(component?: INetworkComponent): Promise<IOfferActiveSubscriptionModel[]> {
        const request = await Network.get(ProductType.PANEL, "/offer/admin/subscriptions/active", component);
        if (request.status === HttpStatus.OK) {
            return request.data;
        }
        return [];
    }

    public static async getClientStats(component?: INetworkComponent): Promise<Record<number, number>> {
        const request = await Network.get(ProductType.PANEL, "/offer/admin/clients/stats", component);
        if (request.status === HttpStatus.OK) {
            return request.data;
        }
        return {};
    }

    public static async updateOfferLimits(data: IOfferLimitsUpdateOutgoing, component?: INetworkComponent): Promise<IOfferAdminModel> {
        Services.handleErrors(component, [
            {status: HttpStatus.NOT_FOUND, message: "Offre non trouvée"},
            {status: HttpStatus.BAD_REQUEST, message: "Erreur lors de la mise à jour des limites"}
        ]);

        const request = await Network.postJson(ProductType.PANEL, "/offer/admin/limits/update", data, component);
        if (request.status === HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async getOfferAdmin(offerId: string, component?: INetworkComponent): Promise<IOfferAdminModel> {
        Services.handleErrors(component, [
            {status: HttpStatus.NOT_FOUND, message: "Offre non trouvée"}
        ]);

        const request = await Network.get(ProductType.PANEL, `/offer/admin/${offerId}`, component);
        if (request.status === HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async updateOfferLimitsBatch(data: IOfferLimitsBatchUpdateOutgoing, component?: INetworkComponent): Promise<number> {
        Services.handleErrors(component, [
            {status: HttpStatus.NOT_FOUND, message: "Erreur lors de la mise à jour"},
            {status: HttpStatus.BAD_REQUEST, message: "Données invalides"}
        ]);

        const request = await Network.postJson(ProductType.PANEL, "/offer/admin/limits/batch", data, component);
        if (request.status === HttpStatus.OK) {
            return request.data;
        }
        return 0;
    }
}